/**
 * @fileoverview Main landing page component - exact reproduction of iscri.html
 * @author ISCRI Team
 */

import { useEffect } from "react";
import HeroSection from "./HeroSection";
import AboutSection from "./AboutSection";
import FeaturesGrid from "./FeaturesGrid";
import InfoSection from "./InfoSection";
import "./styles/LandingPage.css";

/**
 * Main landing page component replicating iscri.html prototype
 * Contains hero, about ISCRI, features, and informational sections
 * @returns {JSX.Element} Landing page component
 */
const LandingPage = () => {
  /**
   * Add reveal animations on scroll
   */
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("reveal-visible");
        }
      });
    }, observerOptions);

    const revealElements = document.querySelectorAll(".reveal-element");
    revealElements.forEach((el) => observer.observe(el));

    return () => {
      revealElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <main className="landing-page">
      <HeroSection />
      <FeaturesGrid />

      <InfoSection
        id="how-calculated"
        title="How is ISCRI calculated?"
        className="info-section"
      >
        <div className="info-text">
          <p>
            The ISCRI can be understood as the cumulative outcomes of past
            interstate conflictual events: a stock fed by flows of conflictual
            events. We produce a Total ISCRI which is composed of a Verbal ISCRI
            and a Material ISCRI.
          </p>

          <p>
            A <strong>verbal conflict</strong> is a spoken criticism, threat, or
            accusation often related to past or future potential acts of
            material conflict.
          </p>

          <p>
            A <strong>material conflict</strong> is a physical act of a
            conflictual nature implying for example the exhibition of force
            posture, reduction of relations, coercion, assault, fight or the use
            of mass violence.
          </p>

          <p>
            Note that we initialise the ISCRI indicator in 1991. More
            information is provided in the Methodology paper (see Methodology
            tab).
          </p>
        </div>
      </InfoSection>

      <InfoSection
        id="comparison"
        title="How is it different from other indicators?"
        className="info-section"
      >
        <div className="info-text">
          <p>
            While our approach is grounded in the events data literature, it
            displays strong similarities with the construction of the
            Geopolitical Risk (GPR) Index proposed by Caldara and Iacovello
            (2022). They calculate the share of articles mentioning adverse
            geopolitical events, based on their own 'geopolitical' dictionary,
            in leading newspapers (10) published in the United States (6), the
            United Kingdom (3), and Canada (1). It is time-specific but is not
            country-specific. Although a country-specific GPR index can be
            calculated, it seems to capture a mix of exposure of countries to
            global risks and domestic conflictual events important enough to be
            reported in the press. In contrast with the GPR index, our approach
            relies on more sources, makes use of a validated dictionary, is
            country-pair specific and focuses on interstate relations. The two
            indexes are likely to be complementary.
          </p>
        </div>
      </InfoSection>

      <InfoSection
        id="interpretation"
        title="How should the ISCRI be interpreted?"
        className="info-section"
      >
        <div className="info-text">
          <p>
            The interpretation of the index is simple but must be
            contextualised. If total ISCRI increases, it means that interstate
            relations are becoming more conflictual. If total ISCRI does not
            change, the conflictual nature of interstate relations is likely to
            be stable. If total ISCRI decreases, it implies that interstate
            relations are becoming less conflictual. A greater understanding of
            the severity of conflict escalation can be achieved by monitoring
            both verbal and material ISCRIs.
          </p>

          <p>
            Note that we do not recommend using the indicator to compare ISCRI
            between countries as each interstate relation is context-specific,
            with events not having the same weight or meaning across country
            pairs, and some interstate relations may receive more media
            attention than others. The real informational value of our indicator
            lies in its time-series variation, which ought to serve as an early
            warning synthetic signal.
          </p>
        </div>
      </InfoSection>

      <InfoSection
        id="interface"
        title="How to use the interface?"
        className="info-section"
      >
        <div className="info-text">
          <p>Two access modes are available: simple and advanced.</p>

          <p>
            The simple use mode is pre-parameterised; while the advanced use
            mode provides much more flexibility.
          </p>
        </div>
      </InfoSection>
    </main>
  );
};

export default LandingPage;